'use client';

import { Session } from 'next-auth';
import { SessionProvider } from 'next-auth/react';
import React from 'react';
import MessageBanner from '@/components/molecules/message-banner/message-banner';
import Footer, { FooterProps } from '@/components/organisms/footer/footer';
import LoginCurtainWrapper from '@/components/organisms/login-curtain/login-curtain-wrapper';
import MainNavigation, {
  MainNavigationProps,
} from '@/components/organisms/main-navigation/main-navigation';
import { ContentSearchSettings } from '@/integrations/content-search/content-search-settings/content-search-settings.fetch';
import { BookingFlowContextProvider } from '@/libraries/contexts/booking-flow-context/booking-flow-context';
import { ContentSearchProvider } from '@/libraries/contexts/content-search-context/content-search-context';
import { FavoritesProvider } from '@/libraries/contexts/favorites-context';
import { HotelContextProvider } from '@/libraries/contexts/hotel-context/hotel-context';
import { PageDetailsProvider } from '@/libraries/contexts/page-details-context/page-details-context';
import { PredefinedSearchContextProvider } from '@/libraries/contexts/predefined-search-context/predefined-search-context';
import { RecentSearchesProvider } from '@/libraries/contexts/recent-searches-context/recent-searches-context';
import { SearchContextProvider } from '@/libraries/contexts/search-context';
import { useSiteSettingsContext } from '@/libraries/contexts/site-settings-context';
import { UserProfileContextProvider } from '@/libraries/contexts/user-profile-context/user-profile-context';
import { useScrollPersistence } from '@/libraries/hooks/use-scroll-persistence';

export interface DefaultPageLayoutProps {
  $type?: string;
  area: string | null;
  bookingType?: Content.Components['BookingType'];
  brand: Content.Properties.Brand;
  children?: React.ReactNode;
  contentSearchSettings?: ContentSearchSettings;
  destinationId?: number;
  enableTransparentNavigation?: boolean;
  footer?: FooterProps;
  hotelDetails?: Content.PageType.HotelPage['hotelDetails'];
  mainNavigation?: MainNavigationProps;
  offerDetails?: Content.PageType.HotelPage['offerDetails'];
  searchContext?: {
    adultsNumber?: string;
    adultsPerRoom?: string;
    bonusCodes?: string;
    childrenAges?: string;
    childrenAgesPerRoom?: string;
    departureDate?: string;
    departureIds?: string;
  };
  searchWidgetState?: Content.Components['SearchWidgetState'];
  session?: Session | null;
  specialHotelType?: Content.Components['SpecialHotelType'];
  trackingData: Content.Properties.TrackingData;
  userProfile?: Content.Components['GetUserProfileResponse'];
}

const DefaultPageLayout = ({
  $type,
  area,
  bookingType,
  brand,
  children,
  contentSearchSettings,
  destinationId,
  enableTransparentNavigation = false,
  footer,
  hotelDetails,
  mainNavigation,
  offerDetails,
  searchContext,
  session,
  searchWidgetState,
  specialHotelType,
  trackingData,
  userProfile,
}: DefaultPageLayoutProps) => {
  const { messageBanner } = useSiteSettingsContext();

  useScrollPersistence();

  return (
    <React.Fragment>
      <SessionProvider basePath="/api/auth" session={session}>
        <PageDetailsProvider
          initialData={{
            bookingType,
            brand,
            destinationId,
            hasTransparentNavigation: enableTransparentNavigation,
            hotelDetails,
            offerDetails,
            pageType: $type,
            searchWidgetState,
            trackingData,
          }}
        >
          <BookingFlowContextProvider>
            <SearchContextProvider
              initialData={{
                area: area ?? '',
                brand: brand,
                searchContext: searchContext,
              }}
            >
              <FavoritesProvider>
                <RecentSearchesProvider>
                  <PredefinedSearchContextProvider>
                    <HotelContextProvider
                      initialData={{
                        specialHotelType: specialHotelType,
                      }}
                    >
                      <ContentSearchProvider
                        initialData={{ contentSearchSettings }}
                      >
                        <UserProfileContextProvider
                          initialData={{ userProfile }}
                        >
                          <div data-brand={brand}>
                            {mainNavigation && (
                              <MainNavigation {...mainNavigation} />
                            )}

                            <main>{children}</main>

                            {footer && <Footer {...footer} />}

                            <LoginCurtainWrapper />
                          </div>
                        </UserProfileContextProvider>
                      </ContentSearchProvider>
                    </HotelContextProvider>
                  </PredefinedSearchContextProvider>
                </RecentSearchesProvider>
              </FavoritesProvider>
            </SearchContextProvider>
          </BookingFlowContextProvider>
        </PageDetailsProvider>
      </SessionProvider>

      {messageBanner && (
        <MessageBanner {...messageBanner} enabled={messageBanner.enable} />
      )}
    </React.Fragment>
  );
};

export default DefaultPageLayout;
